/* eslint-disable eslint-comments/disable-enable-pair, max-lines, security/detect-object-injection */
import deepFreze from 'deep-freeze';
import assignment from 'assignment';

import autocompleteTokensEqual from 'src/utils/autocompleteTokensEqual';


const parseHtmlListItemToAutocompleteLabel = (label) => {
  const element = document.createElement('div');
  element.innerHTML = label;
  const termObject = [...element.childNodes].map(({ tagName, textContent }) => (
    { highlight: !!tagName, part: textContent }
  ));
  return {
    label: termObject,
    text: termObject.map(({ part }) => part).join('')
  };
};

const mapAutocompleteObjectItem = (autocompleteObject, group) => {
  // citi = workplace, its wierd but its true
  const formatCitiesAutocomplete = ({ name, term }) => ({
    name: 'workplace',
    value: name,
    ...parseHtmlListItemToAutocompleteLabel(term)
  });
  const formatCountriesAutocomplete = ({ id, term }) => ({
    name: 'countries',
    value: String(id),
    ...parseHtmlListItemToAutocompleteLabel(term)
  });
  const formatrRegionsAutocomplete = ({ id, term }) => ({
    name: 'regions',
    value: String(id),
    ...parseHtmlListItemToAutocompleteLabel(term)
  });

  // jobTitles = keyword, its wierd but its true
  const formatJobTitlesAutocomplete = ({ name, term }) => ({
    name: 'keyword',
    value: name,
    ...parseHtmlListItemToAutocompleteLabel(term)
  });

  const formatSkillsAutocomplete = ({ name, term }) => ({
    name: 'skills',
    value: name,
    ...parseHtmlListItemToAutocompleteLabel(term)
  });

  const formatrEmployersAutocomplete = ({ name, term }) => ({
    name: 'employers',
    value: name,
    ...parseHtmlListItemToAutocompleteLabel(term)
  });

  const transformators = {
    cities: formatCitiesAutocomplete,
    countries: formatCountriesAutocomplete,
    regions: formatrRegionsAutocomplete,
    jobTitles: formatJobTitlesAutocomplete,
    employers: formatrEmployersAutocomplete,
    skills: formatSkillsAutocomplete
  };
  return (transformators[group] || (() => { }))(autocompleteObject);
};

/* eslint complexity: ["error", 8] */
const formatAutocompleteResponse = (data) => {
  const cities = (data.cities || []).map(region => mapAutocompleteObjectItem(region, 'cities'));
  const regions = (data.regions || []).map(region => mapAutocompleteObjectItem(region, 'regions'));
  const countries = (data.countries || []).map(country => mapAutocompleteObjectItem(country, 'countries'));
  const jobTitles = (data.jobTitles || []).map(jobTitle => mapAutocompleteObjectItem(jobTitle, 'jobTitles'));
  const employers = (data.employers || []).map(employer => mapAutocompleteObjectItem(employer, 'employers'));
  const skills = (data.skills || []).map(skill => mapAutocompleteObjectItem(skill, 'skills'));

  return [
    ...cities,
    ...regions,
    ...countries,
    ...jobTitles,
    ...employers,
    ...skills
  ];
};

const updateWorkplaceAutocompleteSuggestions = (state, action) => {
  deepFreze(state);
  return assignment(
    {},
    state,
    {
      autocomplete: {
        workplace: {
          suggestions: formatAutocompleteResponse(action.state)
        }
      }
    }
  );
};

const updateKeywordAutocompleteSuggestions = (state, action) => {
  deepFreze(state);
  return assignment(
    {},
    state,
    {
      autocomplete: {
        keyword: {
          suggestions: formatAutocompleteResponse(action.state)
        }
      }
    }
  );
};

const makeChipsKeyword = searchParameters => ['keyword',
  'employers',
  'skills'].reduce((token, name) => token.concat(searchParameters[name].map(item => (
  {
    name,
    label: item,
    value: item,
    text: item
  }
))),
[]);
const generateKeywordAutocompleteTokens = (state) => {
  deepFreze(state);
  return assignment(
    {},
    state,
    {
      autocomplete: {
        keyword: {
          tokens: makeChipsKeyword(state.current)
        }
      }
    }
  );
};

const makeChipsWorkplace = (searchParameters, dictionaries) => ['workplace',
  'regions',
  'countries'].reduce((token, name) => token.concat(searchParameters[name].map((item) => {
  const label = name === 'workplace' ? item : dictionaries[name][item] || '';
  return {
    name,
    label,
    value: item,
    text: label
  };
})),
[]);
const generateWorkplaceAutocompleteTokens = (state, action) => {
  deepFreze(state);
  return assignment(
    {},
    state,
    {
      autocomplete: {
        workplace: {
          tokens: makeChipsWorkplace(state.current, action.state)
        }
      }
    }
  );
};

const addNewToken = ({
  newToken, tokens, state, name
}) => {
  const newTokenValue = newToken.value;
  const newTokenName = newToken.name;
  const newValues = [...state.current[newTokenName], newTokenValue];

  const currentTokensWithoutNew = tokens.filter(token => !autocompleteTokensEqual(token, newToken));

  return assignment(
    {},
    state,
    {
      current: {
        [newTokenName]: [...new Set(newValues)]
      },
      autocomplete: {
        [name]: {
          tokens: [...currentTokensWithoutNew, newToken]
        }
      }
    }
  );
};

const addAutocompleteToken = name => (state, action) => {
  deepFreze(state);

  const { suggestions, tokens } = state.autocomplete[name];

  const newToken = suggestions[action.state];
  return addNewToken({
    newToken, tokens, state, name
  });
};

const addAutocompleteCustomToken = name => (state, action) => {
  deepFreze(state);

  const { tokens } = state.autocomplete[name];

  const newToken = {
    name,
    value: action.state,
    label: action.state
  };

  return addNewToken({
    newToken, tokens, state, name
  });
};

const addKeywordAutocompleteCustomToken = addAutocompleteCustomToken('keyword');
const addWorkplaceAutocompleteCustomToken = addAutocompleteCustomToken('workplace');
const addKeywordAutocompleteToken = addAutocompleteToken('keyword');
const addWorkplaceAutocompleteToken = addAutocompleteToken('workplace');

const removeWorkplaceAutocompleteToken = (state, action) => {
  deepFreze(state);
  const workplace = state.current.workplace.filter(item => item !== action.state.value);
  const regions = state.current.regions.filter(item => item !== action.state.value);
  const countries = state.current.countries.filter(item => item !== action.state.value);
  const tokens = state.autocomplete.workplace.tokens
    .filter(token => !(token.value === action.state.value && token.name === action.state.name));

  return assignment(
    {},
    state,
    {
      current: {
        workplace,
        regions,
        countries
      },
      autocomplete: {
        workplace: {
          tokens
        }
      }
    }
  );
};

const removeKeywordAutocompleteToken = (state, action) => {
  deepFreze(state);
  const keyword = state.current.keyword.filter(item => item !== action.state.value);
  const employers = state.current.employers.filter(item => item !== action.state.value);
  const skills = state.current.skills.filter(item => item !== action.state.value);

  const tokens = state.autocomplete.keyword.tokens
    .filter(token => !(token.value === action.state.value && token.name === action.state.name));

  return assignment(
    {},
    state,
    {
      current: {
        keyword,
        employers,
        skills
      },
      autocomplete: {
        keyword: {
          tokens
        }
      }
    }
  );
};

const setAutocompleteLatestSearch = (state, action) => {
  deepFreze(state);

  const keyword = action.state.keyword || [];
  const workplace = action.state.workplace || [];
  return assignment(
    {},
    state,
    {
      autocomplete: {
        keyword: {
          latest: keyword,
          suggestions: keyword
        },
        workplace: {
          latest: workplace,
          suggestions: workplace
        }
      }
    }
  );
};

const showKeywordAutocompleteLatest = (state) => {
  deepFreze(state);
  return assignment(
    {},
    state,
    {
      autocomplete: {
        keyword: {
          suggestions: state.autocomplete.keyword.latest
        }
      }
    }
  );
};
const showWorkplaceAutocompleteLatest = (state) => {
  deepFreze(state);
  return assignment(
    {},
    state,
    {
      autocomplete: {
        workplace: {
          suggestions: state.autocomplete.workplace.latest
        }
      }
    }
  );
};
const updateWorkplaceAutocompleteValue = (state, action) => {
  deepFreze(state);
  return assignment(
    {},
    state,
    {
      autocomplete: {
        workplace: {
          value: action.state
        }
      }
    }
  );
};
const updateKeywordAutocompleteValue = (state, action) => {
  deepFreze(state);
  return assignment(
    {},
    state,
    {
      autocomplete: {
        keyword: {
          value: action.state
        }
      }
    }
  );
};

export {
  updateWorkplaceAutocompleteSuggestions,
  updateKeywordAutocompleteSuggestions,
  generateKeywordAutocompleteTokens,
  generateWorkplaceAutocompleteTokens,
  addWorkplaceAutocompleteToken,
  addKeywordAutocompleteToken,
  addWorkplaceAutocompleteCustomToken,
  addKeywordAutocompleteCustomToken,
  removeWorkplaceAutocompleteToken,
  removeKeywordAutocompleteToken,
  setAutocompleteLatestSearch,
  showKeywordAutocompleteLatest,
  showWorkplaceAutocompleteLatest,
  updateWorkplaceAutocompleteValue,
  updateKeywordAutocompleteValue
};
